/**
 * Two stacked task panels — "Upcoming" (next 5 by dueDate ASC, due
 * today or later) and "Overdue" (top 5 by dueDate ASC, due in the
 * past). Each row is a button so the user can click straight through
 * to the task detail dialog (`onOpenTask` is the same handler the
 * SpacePage's List view rows wire into).
 *
 * Date formatting is local; nothing to bring in dayjs / date-fns.
 * Format: DD MMM (e.g. "23 May") — same convention the rest of the
 * Spaces UX uses on row cells.
 */

import { CalendarClock, AlertTriangle, Flag } from 'lucide-react'

import { formatDateTime } from '../../lib/format'

const PRIORITY_META = {
  urgent: { label: 'Urgent', color: 'var(--color-priority-urgent)' },
  high: { label: 'High', color: 'var(--color-priority-high)' },
  normal: { label: 'Medium', color: 'var(--color-priority-normal)' },
  low: { label: 'Low', color: 'var(--color-priority-low)' },
}

export function SpaceOverviewLists({ overview, onOpenTask }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <TaskPanel
        title="Upcoming"
        icon={CalendarClock}
        rows={overview.upcoming ?? []}
        emptyLabel="Nothing scheduled in the next few days."
        onOpenTask={onOpenTask}
      />
      <TaskPanel
        title="Overdue"
        icon={AlertTriangle}
        accent="var(--color-priority-urgent)"
        rows={overview.overdue ?? []}
        emptyLabel="All clear — no overdue tasks in this Space."
        onOpenTask={onOpenTask}
      />
    </div>
  )
}

function TaskPanel({ title, icon: Icon, accent, rows, emptyLabel, onOpenTask }) {
  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} style={accent ? { color: accent } : undefined} />
        <p className="font-display text-lg font-medium tracking-tight text-[color:var(--color-ink-strong)]">
          {title}
        </p>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-[color:var(--color-ink-muted)]">{emptyLabel}</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => onOpenTask?.(t.id)}
                className="w-full flex items-center gap-3 px-2 py-2 rounded-md text-left hover:bg-[color:var(--color-card-hover)]"
              >
                {t.priority ? (
                  <Flag
                    size={11}
                    fill={PRIORITY_META[t.priority]?.color ?? 'var(--color-ink-subtle)'}
                    strokeWidth={0}
                    className="shrink-0"
                  />
                ) : (
                  <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0 bg-[color:var(--color-ink-subtle)]/40" />
                )}
                <span className="flex-1 truncate text-sm text-[color:var(--color-ink)]">
                  {t.name}
                </span>
                <span className="text-xs tabular-nums text-[color:var(--color-ink-muted)] shrink-0">
                  {fmtShortDate(t.dueDate)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function fmtShortDate(value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return formatDateTime(d)
}
